import React, { useState, useEffect } from "react";
import { FaPaperPlane, FaSpinner, FaTimes, FaRedo, FaEye, FaCheckCircle, FaUpload, FaYoutube, FaVideo, FaGamepad } from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const RunningLeads = () => {
  const navigate = useNavigate();
  const [runningLeads, setRunningLeads] = useState([]);
  const [adViews, setAdViews] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [checkingViews, setCheckingViews] = useState({});

  const stats = {
    runningAds: runningLeads.filter((ad) => ad.runningAdStatus === "running").length,
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/running/ads`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = response.data.data || [];
      setRunningLeads(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data.");
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const openModal = (ad) => {
    setSelectedAd(ad);
    setShowModal(true);
    setConfirmSend(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAd(null);
    setConfirmSend(false);
    setIsSubmitting(false);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleSubmitAd = async () => {
    if (!confirmSend) {
      toast.error("Please confirm the data accuracy");
      return;
    }
    if (!selectedAd) return;
    if (!selectedImage) {
      toast.error("Proof screenshot is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("adId", selectedAd.adId);
      formData.append("reporterId", selectedAd.proofs?.[0]?.reporterId);
      formData.append("channelName", selectedAd.proofs?.[0]?.channelName);
      formData.append("platform", selectedAd.proofs?.[0]?.platform);
      formData.append("adType", selectedAd.adType);
      formData.append("budget", selectedAd.finalReporterPrice);
      formData.append("requiredViews", selectedAd.baseView);
      formData.append("currentViews", adViews[selectedAd.adId] || 0);
      formData.append("duration", selectedAd.proofs?.[0]?.duration);
      formData.append("videoUrl", selectedAd.proofs?.[0]?.videoLink);
      formData.append("status", selectedAd.proofs?.[0]?.status);
      formData.append("submittedAt", selectedAd.proofs?.[0]?.submittedAt);
      formData.append("runningAdStatus", selectedAd.runningAdStatus);
      formData.append("screenshot", selectedImage);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/submit/completed/ad`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success === true) {
        toast.success("Assignment reports submitted successfully!");
        closeModal();
        await fetchLeads();
      } else {
        toast.error("Views requirement not met yet.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkCurrentViews = async (lead, adIndex) => {
    setCheckingViews((prev) => ({ ...prev, [adIndex]: true }));
    try {
      const platform = lead.proofs?.[0]?.platform;
      const videoUrl = lead.proofs?.[0]?.videoLink;
      if (!platform || !videoUrl) {
        toast.error("Project link missing");
        return;
      }
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/check/current/view`,
        { platform, videoUrl },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setAdViews((prev) => ({
        ...prev,
        [lead.adId]: response.data.views || 0
      }));
      toast.success("Intelligence updated");
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setCheckingViews((prev) => ({ ...prev, [adIndex]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Project Radar</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">Scanning Coverage</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-indigo-600 italic leading-none">{stats.runningAds} Live Streams</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchLeads}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-indigo-500" : ""}`} />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-3 space-y-3">
        {/* Live Tracking Warning */}
        {!loading && !error && (
          <div className="bg-indigo-50/50 border border-indigo-100/50 rounded-2xl p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white shrink-0 shadow-sm animate-pulse">
              <FaVideo className="text-[10px]" />
            </div>
            <p className="text-[9px] font-black text-indigo-800 uppercase tracking-wider italic leading-relaxed">System is tracking live engagement. Verify views before submitting final report.</p>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Pinging Satellites...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Project</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Uplink Code</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Platform</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Engagement</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Operations</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {[...runningLeads].reverse().map((lead, index) => (
                  <tr key={index} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center border border-indigo-100/50">
                          <FaGamepad className="text-indigo-600 text-[10px]" />
                        </div>
                        <span className="font-black text-slate-800 text-[10px] tracking-tight italic truncate max-w-[150px]">{lead.proofs?.[0]?.channelName || "Official Broadcast"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-slate-400 font-bold italic tabular-nums uppercase">#{lead.adId}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-blue-50 text-blue-600 border border-blue-100">{lead.proofs?.[0]?.platform}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                          <span className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1">Target</span>
                          <span className="text-[10px] font-black text-slate-800 tabular-nums leading-none tracking-tighter">{lead.baseView}</span>
                        </div>
                        <div className="w-[1px] h-4 bg-slate-100" />
                        <div className="flex flex-col">
                          <span className="text-[7px] font-black text-indigo-400 uppercase leading-none mb-1">Live</span>
                          <span className="text-[10px] font-black text-indigo-600 tabular-nums leading-none tracking-tighter">{adViews[lead.adId] || "0"}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button onClick={() => checkCurrentViews(lead, index)} disabled={checkingViews[index]} className="h-8 px-3 bg-white border border-slate-200 text-slate-600 rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5">
                          {checkingViews[index] ? <FaSpinner className="animate-spin" /> : <FaRedo />} Sync
                        </button>
                        <button
                          onClick={() => openModal(lead)}
                          disabled={!adViews[lead.adId] || adViews[lead.adId] < lead.baseView}
                          className={`h-8 px-4 rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5 ${(!adViews[lead.adId] || adViews[lead.adId] < lead.baseView) ? "bg-slate-50 text-slate-300 border border-slate-100" : "bg-indigo-600 text-white shadow-lg shadow-indigo-100 animate-pulse"}`}
                        >
                          <FaPaperPlane /> Report
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {runningLeads.length > 0 ? (
              [...runningLeads].reverse().map((lead, index) => (
                <div key={index} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-indigo-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-4">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 p-0.5 shadow-md shadow-indigo-100/50 flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaVideo className="text-indigo-500 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{lead.proofs?.[0]?.channelName || "Live Coverage"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest tabular-nums italic">Code #{lead.adId}</p>
                          <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest bg-indigo-100 text-indigo-800">Monitoring</span>
                        </div>
                      </div>
                    </div>

                    {/* Engagement Grid */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white rounded-xl p-3 border border-slate-100/50">
                        <p className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1.5">Target Capacity</p>
                        <p className="text-[14px] font-black text-slate-900 leading-none tabular-nums italic tracking-tighter">{lead.baseView}</p>
                      </div>
                      <div className="bg-slate-900 rounded-xl p-3 shadow-lg shadow-indigo-100/20 relative overflow-hidden group">
                        <button onClick={() => checkCurrentViews(lead, index)} disabled={checkingViews[index]} className="absolute inset-0 z-10 w-full h-full flex items-center justify-center bg-indigo-600/90 text-white opacity-0 group-active:opacity-100 transition-opacity">
                          <FaRedo className={`text-xs ${checkingViews[index] ? 'animate-spin' : ''}`} />
                        </button>
                        <p className="text-[7px] font-black text-indigo-400 uppercase leading-none mb-1.5">Live Reach</p>
                        <p className="text-[14px] font-black text-white leading-none tabular-nums italic tracking-tighter">{adViews[lead.adId] || "0"}</p>
                      </div>
                    </div>

                    {/* Actions Group */}
                    <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                      <div className="flex-1 flex flex-col gap-1">
                        <span className="text-[7px] font-black text-slate-300 uppercase leading-none italic">Verified Reach</span>
                        <span className={`text-[9px] font-black uppercase ${(!adViews[lead.adId] || adViews[lead.adId] < lead.baseView) ? "text-rose-400" : "text-emerald-500"}`}>
                          {(!adViews[lead.adId] || adViews[lead.adId] < lead.baseView) ? "Incomplete Data" : "Requirement Met"}
                        </span>
                      </div>
                      <button
                        onClick={() => openModal(lead)}
                        disabled={!adViews[lead.adId] || adViews[lead.adId] < lead.baseView}
                        className={`h-10 px-5 rounded-xl font-black uppercase tracking-widest text-[9px] flex items-center gap-2 active:scale-95 transition-all ${(!adViews[lead.adId] || adViews[lead.adId] < lead.baseView) ? "bg-slate-100 text-slate-300" : "bg-indigo-600 text-white shadow-lg shadow-indigo-100"}`}
                      >
                        <FaPaperPlane /> Submit report
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-indigo-50 flex items-center justify-center mb-6">
                  <FaVideo className="text-indigo-200 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">No Active Streams</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed">No coverage projects currently transmitting in the monitoring grid.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Critical submission Modal */}
      {showModal && selectedAd && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-all" onClick={closeModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-slate-900 p-7 text-white relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl" />
              <h3 className="text-xl font-black tracking-tighter leading-tight mb-1 relative z-10">Uplink Verification</h3>
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 italic relative z-10">Final mission report transmission</p>
              <button onClick={closeModal} className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all"><FaTimes className="text-xs" /></button>
            </div>

            <div className="p-7 space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar">
              {/* Proof Image Upload */}
              <div className="space-y-3">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Engagement Evidence (Screenshot)</p>
                <div className={`relative w-full border-2 border-dashed rounded-[2rem] transition-all flex flex-col items-center justify-center p-6 ${imagePreview ? 'border-indigo-200 bg-indigo-50/20' : 'border-slate-100 bg-slate-50 hover:border-indigo-100 hover:bg-slate-100/50'}`}>
                  {imagePreview ? (
                    <div className="relative w-full">
                      <img src={imagePreview} alt="Intel" className="w-full h-40 object-cover rounded-2xl shadow-lg border border-white" />
                      <button type="button" onClick={() => { setSelectedImage(null); setImagePreview(null); }} className="absolute -top-2 -right-2 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all"><FaTimes className="text-xs" /></button>
                    </div>
                  ) : (
                    <>
                      <input type="file" accept="image/*" onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setSelectedImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setImagePreview(reader.result);
                          reader.readAsDataURL(file);
                        }
                      }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                      <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3">
                        <FaUpload className="text-indigo-500 text-xs animate-bounce" />
                      </div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter text-center">Capture live views & upload screenshot evidence</p>
                    </>
                  )}
                </div>
              </div>

              {/* Intel Summary */}
              <div className="grid grid-cols-2 gap-3 pb-2 border-b border-slate-50">
                <div className="space-y-1">
                  <p className="text-[7px] font-black text-slate-400 uppercase">Target views</p>
                  <p className="text-[11px] font-black text-slate-800 tabular-nums italic tracking-tighter">{selectedAd.baseView}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[7px] font-black text-indigo-400 uppercase">Synced Reach</p>
                  <p className="text-[11px] font-black text-indigo-600 tabular-nums italic tracking-tighter">{adViews[selectedAd.adId] || "0"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[7px] font-black text-blue-400 uppercase">Uplink Status</p>
                  <p className="text-[11px] font-black text-blue-600 truncate italic">{selectedAd.proofs?.[0]?.status}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[7px] font-black text-purple-400 uppercase">Broadcast Channel</p>
                  <p className="text-[11px] font-black text-purple-600 truncate italic">{selectedAd.proofs?.[0]?.channelName}</p>
                </div>
              </div>

              {/* Confirmation */}
              <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-100/50 flex items-start gap-4 active:scale-95 transition-all select-none cursor-pointer" onClick={() => setConfirmSend(!confirmSend)}>
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all shrink-0 ${confirmSend ? 'bg-indigo-600 text-white' : 'bg-white border-2 border-indigo-100 text-transparent'}`}><FaCheckCircle className="text-[10px]" /></div>
                <div>
                  <p className="text-[10px] font-black text-indigo-900 uppercase tracking-widest leading-none mb-1">Affirmative Confirmation</p>
                  <p className="text-[9px] font-bold text-indigo-400 leading-tight italic uppercase">I verify that the captured engagement data is accurate and meets mission requirements.</p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-slate-50 flex items-center gap-3 px-8 border-t border-slate-100">
              <button onClick={closeModal} className="flex-1 h-12 bg-white border border-slate-200 text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-widest">Abandon</button>
              <button onClick={handleSubmitAd} disabled={!confirmSend || isSubmitting || !selectedImage} className="flex-[2] h-12 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.3em] shadow-lg shadow-slate-200 active:scale-95 transition-all disabled:opacity-20">Uplink report</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default RunningLeads;
